import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { labelGrid } from '../utils';

function formatCnpjCpf(value: string): string {
    if (!value) return "-";

    const normalizado = value
        .normalize("NFKC")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .toUpperCase()
        .replace(/[./\-\s]/g, "");

    if (/^\d{11}$/.test(normalizado)) {
        return normalizado.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    if (/^[0-9A-Z]{12}\d{2}$/.test(normalizado)) {
        return normalizado.replace(/([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return value;
}

function CustomTableSelectAgentes(props: {
    data: any[], buscouAgentes: boolean, handleSetAgente: (agente: any) => void, isAgente: boolean,
    handleEditarAgente: (agente: any) => void,
    handleVisualizarAgente?: (agente: any) => void
}) {
    const rows = props.data;
    const showCollsTable = props.isAgente
        ? ["codAgente", "nome", "cnpjCpf", "nomeFantasia", "nomeCidade", "fone1"]
        : ["codAgente", "nome", "cnpjCpf", "nomeFantasia", "nomeCidade"];
    const StyledTableCell = styled(TableCell)(() => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: '#e9f2fd',
            color: '#0f3550',
            fontSize: '12px',
            letterSpacing: '0.6px',
            padding: '4px 16px',
            marginTop: '-5px',
            borderBottom: '2px solid #d9e5ee',
            position: 'sticky' as const,
            top: 0,
            zIndex: 2,
            whiteSpace: 'nowrap' as const,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: '12px',
            color: '#334155',
            padding: '5px 16px',
            borderBottom: '1px solid #E2E8F0',
        },
    }));

    const StyledTableRow = styled(TableRow)(() => ({
        height: '32px',
        '&:nth-of-type(odd)': {
            backgroundColor: '#f8fafc',
        },
        '&:hover': {
            backgroundColor: '#eef4fb',
            transition: 'background-color 0.15s ease',
        },
        '&:last-child td, &:last-child th': {
            borderBottom: 0,
        },
    }));


    return (
        <TableContainer
            component={Paper}
            sx={{
                height: '100%',
                maxHeight: '100%',
                overflowY: 'auto',
                overflowX: 'auto',
                boxShadow: 'none',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
            }}
        >
            <Table aria-label="customized table" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ width: 80, whiteSpace: "nowrap", textAlign: "left !important" }}>{labelGrid.cod}</StyledTableCell>
                            <StyledTableCell sx={{ width: 250, textAlign: "left !important" }}>{labelGrid.nome}</StyledTableCell>
                            <StyledTableCell sx={{ width: 160, whiteSpace: "nowrap", textAlign: "left !important" }}>{labelGrid.cnpjCpf}</StyledTableCell>
                            <StyledTableCell sx={{ width: 190, textAlign: "left !important" }}>{labelGrid.nomeFantasia}</StyledTableCell>
                            <StyledTableCell sx={{ width: 50, textAlign: "left !important" }}>{labelGrid.nomeCidade}</StyledTableCell>
                            {props.isAgente && (
                                <StyledTableCell sx={{ width: 80, textAlign: "left !important" }}>{labelGrid.fone}</StyledTableCell>
                            )}
                            <StyledTableCell sx={{ width: 96, textAlign: "center !important" }}>{labelGrid.opcoes}</StyledTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows && rows.length > 0 ? (
                            rows.map((row: any, index: number) => (
                                <StyledTableRow key={index}>
                                    {showCollsTable.map((col) => {
                                        const valor = col === "cnpjCpf" ? formatCnpjCpf(row[col]) : row[col] ?? null;
                                        return (
                                            <TableCell key={col} align="left" sx={{ fontSize: '12px', color: '#334155', padding: '5px 16px', borderBottom: '1px solid #E2E8F0' }}>
                                                {valor !== null && valor !== undefined && valor !== ''
                                                    ? valor
                                                    : <span style={{ color: '#94a3b8', fontSize: '12px' }}>N/D</span>}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell align="center" sx={{ width: 96, padding: '2px 8px', borderBottom: '1px solid #E2E8F0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
                                            {!props.isAgente &&
                                                <Tooltip title="Adicionar">
                                                    <IconButton
                                                        onClick={() => props.handleSetAgente(row)}
                                                        size="small"
                                                        sx={{ color: '#0965c1', '&:hover': { color: '#1D4ED8', backgroundColor: 'rgba(37,99,235,0.08)' } }}
                                                    >
                                                        <AddCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            {props.isAgente && (() => {
                                                const isEditable = (row.representanteEditaAgen === "S" && row.ativo === "S");
                                                return (
                                                    <Tooltip title={isEditable ? 'Editar' : 'Sem permissão'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!isEditable}
                                                                onClick={isEditable ? () => props.handleEditarAgente(row) : undefined}
                                                                sx={{
                                                                    color: isEditable ? '#0965c1' : '#cbd5e1',
                                                                    '&:hover': { color: '#1D4ED8', backgroundColor: 'rgba(37,99,235,0.08)' },
                                                                    '&.Mui-disabled': { color: '#cbd5e1' },
                                                                }}
                                                            >
                                                                <EditIcon sx={{ fontSize: '17px' }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })()}
                                            {props.isAgente && (
                                                <Tooltip title="Visualizar">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => props.handleVisualizarAgente?.(row)}
                                                        sx={{ color: '#0965c1', '&:hover': { color: '#1D4ED8', backgroundColor: 'rgba(37,99,235,0.08)' } }}
                                                    >
                                                        <VisibilityIcon sx={{ fontSize: '17px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </StyledTableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={showCollsTable.length + 1} align="center" sx={{ py: 6, borderBottom: 0 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                        <SearchOffIcon sx={{ fontSize: 36, color: '#94a3b8' }} />
                                        <Typography sx={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                                            Nenhum agente encontrado
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                                            Tente ajustar os filtros de busca
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
        </TableContainer>
    );
}

export default CustomTableSelectAgentes;
